import type { Integration } from "@repo/app-types/entities/integration";
import { IntegrationProvider } from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Label } from "@repo/ui/Label/label";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { Size, capitalize } from "@repo/ui/helpers/helpers";
import { IconCircleCheck } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { openOAuthSigninPopupForIntegration } from "../../../helpers/urlHelpers.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { deleteIntegration } from "../../integrations/integrationsSlice.js";

export const OAuthIntegrationButtonsRow: FC<{ integration: Integration }> = ({
  integration,
}) => {
  const dispatch = useAppDispatch();
  const [isRemoveAccountModalOpen, setIsRemoveAccountModalOpen] =
    useState(false);

  return (
    <div>
      <Label size={Size.SMALL}>Account</Label>
      <div className="flex flex-row items-center space-x-3 text-sm">
        <div className="flex flex-row items-center space-x-1">
          <IconCircleCheck className="h-5 w-5 text-green-500" />
          <div>
            {capitalize(integration.provider)} account successfully connected
          </div>
        </div>
        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              onClick={() =>
                openOAuthSigninPopupForIntegration(integration.provider)
              }
              size={Size.SMALL}
              variant={ButtonVariant.Secondary}
              isDisabled={!allowed}
            >
              Re-authenticate
            </Button>
          )}
        </Can>
        <Can
          I={PermissionActions.DELETE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              onClick={() => {
                setIsRemoveAccountModalOpen(true);
              }}
              size={Size.SMALL}
              variant={ButtonVariant.Warning}
              isDisabled={!allowed}
            >
              Remove
            </Button>
          )}
        </Can>
      </div>
      <SimpleModal
        isOpen={isRemoveAccountModalOpen}
        onCancel={() => {
          setIsRemoveAccountModalOpen(false);
        }}
        variant={SimpleModalVariant.Warning}
        title="Are you sure?"
        subtitle={getRemoveAccountWarningMessage(integration.provider)}
        confirmButtonText="Remove account"
        onConfirm={() => {
          dispatch(deleteIntegration(integration.id));

          setIsRemoveAccountModalOpen(false);
        }}
      />
    </div>
  );
};

function getRemoveAccountWarningMessage(provider: IntegrationProvider) {
  switch (provider) {
    case IntegrationProvider.SALESFORCE:
      return "Removing your Salesforce account will stop all data from syncing.";
    case IntegrationProvider.SLACK:
      return "Removing your account will stop all interview notifications from being sent to Slack.";
    default:
      return "";
  }
}
